import { useEffect, useState } from "react";
import type { Invoice } from "@/lib/api/types";

export interface PreBillCheckPrint {
  /** The invoice the `printCheck` command returned — the record's own figures. */
  invoice: Invoice;
  splitGuests: number | null;
}

/**
 * Owns the pre-bill check's paper and its print request (round 5).
 *
 * The browser print path, the same shape the refund slip uses: the page
 * records the check through the command envelope FIRST, then hands the
 * returned invoice here; this mounts the paper, waits one frame after commit
 * for the print stylesheet, prints under `data-print-scope="check"` and
 * restores the scope — also on unmount, so leaving mid-print cannot strand the
 * whole app in the check's scope.
 *
 * Why not a durable print job is recorded in `docs/restaurant-printing.md`
 * (§11). A counter rather than a boolean, so a reprint of the same check is a
 * second piece of paper and not a no-op.
 */
export function usePreBillCheckPrinting() {
  const [paper, setPaper] = useState<PreBillCheckPrint | null>(null);
  const [request, setRequest] = useState(0);

  useEffect(() => {
    if (request === 0) return;
    document.body.dataset.printScope = "check";
    const frame = requestAnimationFrame(() => {
      try {
        window.print();
      } finally {
        delete document.body.dataset.printScope;
      }
    });
    return () => {
      cancelAnimationFrame(frame);
      delete document.body.dataset.printScope;
    };
  }, [request]);

  function print(next: PreBillCheckPrint) {
    setPaper(next);
    setRequest((value) => value + 1);
  }

  return { paper, print };
}
